import axios from "axios";
import { useState, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Table,
    Spin,
    Typography,
    message,
    Input,
    Select,
    DatePicker,
    Row,
    Col,
    Modal,
    Button,
} from "antd";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { NavigationContext } from "../../context/NavigationContext";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// How many records to fetch per page.
// Adjust to match what your backend accepts as ?page_size=
const PAGE_SIZE = 20;

export default function EmpCompoff() {
    // Current page of records rendered in the table
    const [compoffs, setCompoffs] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    const [loading, setLoading] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCompoff, setSelectedCompoff] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const [cancelReason, setCancelReason] = useState("");
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);

    useEffect(() => {
        if (navigationParams?.status) {
            setFilters((prev) => ({ ...prev, status: navigationParams.status === "Pending" ? "Pending Approval" : navigationParams.status }));
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    // Bulk approval states
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isBulkApproveModalOpen, setIsBulkApproveModalOpen] = useState(false);
    const [bulkApprovalReason, setBulkApprovalReason] = useState("Approved");

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [filters, setFilters] = useState({
        name: "",
        emp_code: "",
        status: "",
        dateRange: [],
        organisation: "",
        department: "",
        designation: "",
    });

    // Guards against duplicate concurrent fetches
    const fetchingRef = useRef(false);

    // -------------------------------
    //          FETCH COMPOFFS
    // -------------------------------
    // Fetches a single page and replaces the table's dataSource with it.
    const fetchCompoffs = async (currentFilters = filters, page = 1) => {
        if (fetchingRef.current) return;

        fetchingRef.current = true;
        setLoading(true);

        try {
            const params = {
                page,
                page_size: PAGE_SIZE,
            };

            if (currentFilters.name) params.emp_name = currentFilters.name;
            if (currentFilters.emp_code) params.emp_code = currentFilters.emp_code;
            if (currentFilters.organisation) params.organisation = currentFilters.organisation;
            if (currentFilters.department) params.department = currentFilters.department;
            if (currentFilters.designation) params.designation = currentFilters.designation;
            if (currentFilters.status) params.status = currentFilters.status;

            if (currentFilters.dateRange?.length === 2) {
                params.from_date = currentFilters.dateRange[0].format("YYYY-MM-DD");
                params.to_date = currentFilters.dateRange[1].format("YYYY-MM-DD");
            }

            const res = await axios.get(`${backUrl}/api/hr/compoffs/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
                params,
            });

            const results = res.data.results ?? [];
            const count = res.data.count ?? results.length;

            setCompoffs(results);
            setTotalCount(count);
            setCurrentPage(page);
        } catch (error) {
            console.error(error);
            message.error("Failed to load compoff records");
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    };

    // Reset to page 1 + refetch whenever filters change
    useEffect(() => {
        fetchCompoffs(filters, 1);
        // eslint-disable-next-line
    }, [filters]);

    useEffect(() => {
        fetchOrganisation();
    }, []);

    // Table pagination control -> fetch the requested page
    const handlePageChange = (page) => {
        setSelectedRowKeys([]);
        fetchCompoffs(filters, page);
    };

    // -------------------------------
    //    FETCH ORG, DEPT, DESIGNATION
    // -------------------------------
    const fetchOrganisation = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            const results = res.data?.results ?? res.data ?? [];
            setOrganisations(results);
        } catch (err) {
            console.error("Error fetching organisations:", err);
            toast.error("Failed to load organisations");
        }
    };

    useEffect(() => {
        const orgValue = filters.organisation;
        if (orgValue) {
            fetchDepartments(orgValue);
        } else {
            setDepartments([]);
            setDesignations([]);
            setFilters((prev) => ({ ...prev, department: "", designation: "" }));
        }
        // eslint-disable-next-line
    }, [filters.organisation]);

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            const results = res.data?.results ?? res.data ?? [];
            setDepartments(results);
        } catch (err) {
            console.error("Error fetching departments:", err);
            toast.error("Failed to load departments");
        }
    };

    useEffect(() => {
        const deptValue = filters.department;
        if (deptValue) {
            fetchDesignations(deptValue);
        } else {
            setDesignations([]);
            setFilters((prev) => ({ ...prev, designation: "" }));
        }
        // eslint-disable-next-line
    }, [filters.department]);

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            const results = res.data?.results ?? res.data ?? [];
            setDesignations(results);
        } catch (err) {
            console.error("Error fetching designations:", err);
            toast.error("Failed to load designations");
        }
    };

    const updateFilter = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    // -------------------------------
    //        BULK APPROVAL
    // -------------------------------
    const handleBulkApproveClick = () => {
        if (selectedRowKeys.length === 0) {
            toast.warning("Please select at least one compoff to approve");
            return;
        }
        setIsBulkApproveModalOpen(true);
    };

    const handleBulkApproveConfirm = async () => {
        if (!bulkApprovalReason.trim()) {
            toast.error("Please provide an approval reason");
            return;
        }

        try {
            await axios.post(
                `${backUrl}/api/hr/compoffs/approve-work/?is_employee_view=true`,
                {
                    compoff_ids: selectedRowKeys,
                    rejection_reason: bulkApprovalReason,
                },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success(`Successfully approved ${selectedRowKeys.length} compoff(s)`);
            setSelectedRowKeys([]);
            setBulkApprovalReason("Approved");
            setIsBulkApproveModalOpen(false);
            fetchCompoffs(filters, currentPage);
        } catch (error) {
            console.error("Bulk approval error:", error);
            toast.error(
                error.response?.data?.message || "Failed to approve compoffs"
            );
        }
    };

    const handleBulkApproveCancel = () => {
        setIsBulkApproveModalOpen(false);
        setBulkApprovalReason("Approved");
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
        getCheckboxProps: () => ({}),
    };

    // -------------------------------
    //          MODAL ACTIONS
    // -------------------------------
    const handleEditClick = (record) => {
        setSelectedCompoff(record);
        setNewStatus(record.status);
        setCancelReason("");
        setIsModalOpen(true);
    };

    const downloadExcel = () => {
        // Exports the records currently loaded on this page.
        const rows = compoffs.map((item) => {
            const emp = item?.employee ?? {};

            return {
                "Employee Name": emp.full_name ?? "-",
                "Employee Code": emp.emp_code ?? "-",
                Organisation: emp.organisation_name ?? emp.organisation ?? "-",
                Department: emp.department_name ?? emp.department ?? "-",
                Designation: emp.designation_name ?? emp.designation ?? "-",
                "Date Worked": item.work_date
                    ? new Date(item.work_date).toLocaleDateString("en-IN")
                    : "-",
                "Expiry Date": item.expiry_date
                    ? new Date(item.expiry_date).toLocaleDateString("en-IN")
                    : "-",
                Reason: item.reason ?? "-",
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Off Day");
        XLSX.writeFile(workbook, "Employee_Off_day.xlsx");
    };

    const handleModalOk = async () => {
        if (newStatus === "Cancelled" && !cancelReason.trim()) {
            toast.error("Please provide a cancellation reason before saving.");
            return;
        }

        try {
            await axios.post(
                `${backUrl}/api/hr/compoffs/${selectedCompoff.id}/cancel/`,
                { cancellation_reason: cancelReason },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success("Compoff status updated");
            fetchCompoffs(filters, currentPage);
        } catch (error) {
            console.error(error);
            toast.error("Failed to update compoff status.");
        }

        setIsModalOpen(false);
        setSelectedCompoff(null);
    };

    const handleModalCancel = () => {
        setIsModalOpen(false);
        setSelectedCompoff(null);
        setCancelReason("");
    };

    const columns = [
        { title: "Employee Name", dataIndex: ["employee", "full_name"], key: "name", fixed: "left" },
        { title: "Employee Code", dataIndex: ["employee", "emp_code"], key: "code" },
        { title: "Organisation", dataIndex: ["employee", "organisation_name"], key: "org" },
        { title: "Department", dataIndex: ["employee", "department_name"], key: "dept" },
        { title: "Designation", dataIndex: ["employee", "designation_name"], key: "desig" },
        {
            title: "Date Worked",
            dataIndex: "work_date",
            key: "work_date",
            render: (date) => new Date(date).toLocaleDateString("en-IN"),
        },
        {
            title: "Expiry Date",
            dataIndex: "expiry_date",
            key: "expiry_date",
            render: (date) => new Date(date).toLocaleDateString("en-IN"),
        },
        { title: "Reason", dataIndex: "reason", key: "reason" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <span
                    style={{
                        color:
                            status === "Available"
                                ? "green"
                                : status === "Rejected"
                                    ? "red"
                                    : status === "Availed"
                                        ? "orange"
                                        : "blue",
                        fontWeight: "bold",
                    }}
                >
                    {status}
                </span>
            ),
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <ToastContainer />
            <Title level={4} style={{ marginBottom: 16, textTransform: "uppercase", color: "#0e497b" }}>
                Employee Off Day Records
            </Title>

            {/* FILTERS */}
            <div style={{ background: "#fff", padding: "15px", borderRadius: "10px", marginBottom: "15px" }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder="Employee Name"
                            value={filters.name}
                            onChange={(e) => updateFilter("name", e.target.value)}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder="Employee Code"
                            value={filters.emp_code}
                            onChange={(e) => updateFilter("emp_code", e.target.value)}
                        />
                    </Col>

                    {/* Organisation */}
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            value={filters.organisation || undefined}
                            allowClear
                            onChange={(v) => updateFilter("organisation", v)}
                            style={{ width: "100%" }}
                        >
                            {Organisations.map((org) => (
                                <Option key={org.id} value={org.id}>
                                    {org.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    {/* Department */}
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            value={filters.department || undefined}
                            allowClear
                            onChange={(v) => updateFilter("department", v)}
                            disabled={!filters.organisation}
                            style={{ width: "100%" }}
                        >
                            {departments.map((dept) => (
                                <Option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    {/* Designation */}
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            value={filters.designation || undefined}
                            allowClear
                            onChange={(v) => updateFilter("designation", v)}
                            disabled={!filters.department}
                            style={{ width: "100%" }}
                        >
                            {designations.map((des) => (
                                <Option key={des.id} value={des.id}>
                                    {des.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    {/* Status */}
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            value={filters.status || undefined}
                            allowClear
                            onChange={(v) => updateFilter("status", v)}
                            style={{ width: "100%" }}
                        >
                            <Option value="Availed">Availed</Option>
                            <Option value="Available">Available</Option>
                            <Option value="Expired">Expired</Option>
                        </Select>
                    </Col>

                    {/* Date Range */}
                    <Col xs={24} sm={24} md={6}>
                        <RangePicker
                            value={filters.dateRange}
                            format="DD/MM/YYYY"
                            onChange={(dates) => updateFilter("dateRange", dates || [])}
                            style={{ width: "100%" }}
                        />
                    </Col>
                </Row>
            </div>

            {/* ACTION BUTTONS */}
            <div style={{ textAlign: "right", marginBottom: 12, display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <Button
                    type="primary"
                    style={{ background: "#0e497b", borderColor: "#0e497b" }}
                    onClick={downloadExcel}
                >
                    Download Excel ({compoffs.length} loaded)
                </Button>
            </div>

            {/* TABLE */}
            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <Table
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={compoffs}
                        rowKey={(record) => record.id}
                        bordered
                        pagination={{
                            current: currentPage,
                            pageSize: PAGE_SIZE,
                            total: totalCount,
                            showSizeChanger: false,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                            onChange: handlePageChange,
                        }}
                        scroll={{ x: "max-content" }}
                        components={{
                            header: {
                                cell: (props) => (
                                    <th
                                        {...props}
                                        style={{
                                            backgroundColor: "#0e497b",
                                            color: "white",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {props.children}
                                    </th>
                                ),
                            },
                        }}
                    />
                </div>
            )}

            {/* BULK APPROVAL MODAL */}
            <Modal
                title="Bulk Approve Compensatory Off"
                open={isBulkApproveModalOpen}
                onOk={handleBulkApproveConfirm}
                onCancel={handleBulkApproveCancel}
                okText="Approve"
                centered
                okButtonProps={{
                    style: { backgroundColor: "#0e497b", borderColor: "#0e497b", color: "#fff" },
                }}
            >
                <p>
                    You are about to approve <strong>{selectedRowKeys.length}</strong> compoff record(s).
                </p>
                <Input.TextArea
                    placeholder="Enter approval reason (e.g., Approved)"
                    value={bulkApprovalReason}
                    onChange={(e) => setBulkApprovalReason(e.target.value)}
                    rows={3}
                />
            </Modal>

            {/* CANCEL MODAL */}
            <Modal
                title="Edit Compoff Status"
                open={isModalOpen}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="Save"
                centered
                okButtonProps={{
                    style: { backgroundColor: "#0e497b", borderColor: "#0e497b", color: "#fff" },
                }}
            >
                <p>
                    <strong>Employee:</strong> {selectedCompoff?.employee?.first_name} (
                    {selectedCompoff?.employee?.emp_code})
                </p>

                <Select
                    value={newStatus}
                    onChange={(value) => setNewStatus(value)}
                    style={{ width: "100%", marginBottom: "10px" }}
                >
                    <Option value="Cancelled">Cancel</Option>
                </Select>

                {newStatus === "Cancelled" && (
                    <Input.TextArea
                        placeholder="Enter cancel reason"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={3}
                    />
                )}
            </Modal>
        </div>
    );
}